import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { affiliateService, AdminAffiliateStats, AffiliateInfo, AdminReferral, FlaggedActivity } from '@/services/affiliateService';

export const useAdminAffiliateStats = () => {
  return useQuery({
    queryKey: ['adminAffiliateStats'],
    queryFn: () => affiliateService.getAdminAffiliateStats(),
  });
};

export const useAdminAffiliates = (statusFilter: string = 'all') => {
  return useQuery({
    queryKey: ['adminAffiliates', statusFilter],
    queryFn: () => affiliateService.getAdminAffiliates(statusFilter),
  });
};

export const useUpdateAffiliateStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ 
      affiliateId, 
      status 
    }: { 
      affiliateId: string; 
      status: 'active' | 'suspended' | 'pending'; 
    }) => affiliateService.updateAffiliateStatus(affiliateId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminAffiliates'] });
      queryClient.invalidateQueries({ queryKey: ['adminAffiliateStats'] });
    },
  });
};

export const useUpdateCommissionRate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ 
      affiliateId, 
      rate 
    }: { 
      affiliateId: string; 
      rate: number; 
    }) => affiliateService.updateAffiliateCommissionRate(affiliateId, rate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminAffiliates'] });
    },
  });
};

export const useAdminReferrals = (statusFilter: string = 'all', page: number = 1, pageSize: number = 50) => {
  return useQuery({
    queryKey: ['adminReferrals', statusFilter, page, pageSize],
    queryFn: () => affiliateService.getAdminReferrals(statusFilter, page, pageSize),
    staleTime: 30000, // 30 seconds - data stays fresh longer
    gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache longer
  });
};

export const useFlaggedActivities = () => {
  return useQuery({
    queryKey: ['flaggedActivities'],
    queryFn: () => affiliateService.getFlaggedActivities(),
  });
};

export const useDismissFlaggedActivity = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => affiliateService.dismissFlaggedActivity(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flaggedActivities'] });
      queryClient.invalidateQueries({ queryKey: ['adminAffiliateStats'] });
    },
  });
};

export const useMarkFlaggedActivityAsInvestigating = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => affiliateService.markFlaggedActivityAsInvestigating(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flaggedActivities'] });
    },
  });
};
